import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  NodePath,
  ViewQueryList,
  ViewQueryNode,
  ViewQueryRecordNode,
} from '@salescore/core'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { useMemo } from 'react'

import type { RSheetColumn, SheetColumn } from '../../domain/column'
// eslint-disable-next-line sonarjs/deprecation
import { getNode, getRecordNodeFromRootNode } from '../../state/nodeUtility'
import { isSameNodeProperty, queryFieldToConfigField } from '../view/mutations/field/util'
import { buildNamesWithColors } from '../view/selectors/querySelector'
import { useFlattenFilterNodeLeafs, useViewConfigSheet } from '../viewConfig'

// eslint-disable-next-line sonarjs/todo-tag
// TODO: sheetUi.columns = RSheetColumnにして、変換が不要になるようにしたい
export const useSheetColumns = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  query: ViewQueryList,
): Array<SheetColumn<ViewQueryRecordNode>> => {
  const nodeNamesWithColors = useMemo(() => buildNamesWithColors(query), [query])
  const config = useViewConfigSheet()
  const flattenFilterNodeLeafs = useFlattenFilterNodeLeafs(config)
  const [sheetUi] = compiledConfig.ui

  return useMemo(() => {
    if (sheetUi.type !== 'Sheet') {
      return []
    }
    return sheetUi.columns
      .map((column, index): SheetColumn<ViewQueryRecordNode> | undefined => {
        const field = query.fields.find((x) => x.name === column.fieldName)
        if (field === undefined) {
          return undefined
        }
        const configField = queryFieldToConfigField(field, config.fields ?? [])

        const { nodePath } = field
        const nodePaths = Array.dedupeWith(
          nodePath.reduce<NodePath[]>((paths, nodeName) => {
            const lastPath = paths.at(-1) ?? []
            const currentPath = [...lastPath, nodeName]
            return [...paths, currentPath]
          }, []),
          (a, b) => a.join(',') === b.join(','),
        )
        // eslint-disable-next-line sonarjs/deprecation
        const nodes = nodePaths.map((path) => getNode(query.tree, path)).filter(isNotNullish)
        // eslint-disable-next-line sonarjs/todo-tag
        const node = nodes.at(-1) ?? query.tree // TODO: なかった場合適当にルートにしている
        const configIndex =
          config.fields !== undefined && configField !== undefined
            ? config.fields.findIndex((x) => isSameNodeProperty(x.property, configField.property))
            : undefined // config.field 内でのindex
        return {
          columnType: 'sheet',
          index, // 後で上書きしている
          configIndex: configIndex === -1 ? undefined : configIndex,
          field,
          node,
          nodeBlock: {
            nodePath: node.path,
            labels: nodes.map((x) => x.meta.label),
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            colors: nodes.map((x) => nodeNamesWithColors[x.name]!),
            // onClick // menuにするはずだから、menuを実装すればいい？検討
          },
          key: `field-${field.name}`,
          title: column.label,
          helpText: column.description,
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: この辺のtree系の要素をuiにいれないと、query/uiを完全に分離できなそう
          nodeType: convertRelationToNodeType(node),
          color: column.fieldType === 'notFound' ? 'gray' : nodeNamesWithColors[node.name],

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          width: column.width ?? (column.fieldType === 'numeric' ? 100 : 300),
          type: column.fieldType,
          metaType: column.fieldMetaType,
          selectOptions: column.selectOptions,
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: ここでのvalue,labelの実装と、convertRecordNodeToSheetRowでのvalue実装が重複してしまっている。。統一したい
          value: (recordNode: ViewQueryRecordNode) => recordNode.attributes[field.name],
          label:
            column.fieldMetaType === 'relation'
              ? (recordNode: ViewQueryRecordNode): unknown => recordNode.attributes[`${field.name}_label`]
              : undefined,
          highlight: (recordNode: ViewQueryRecordNode): boolean | boolean[] =>
            // eslint-disable-next-line sonarjs/todo-tag
            getHighlight(recordNode, nodePath, field.write?.propertySourceName), // TODO
          isFiltered:
            configField !== undefined &&
            flattenFilterNodeLeafs.some(
              (leaf) => leaf.type === 'property' && isSameNodeProperty(leaf.property, configField.property),
            ),
          isHighlighted:
            (configField?.conditionalEffects ?? []).flatMap((x) => (x.effect === 'highlight' ? [x] : [])).length > 0,
          sortState: query.sorters.find((sorter) => sorter.read.sql === field.read.sql)?.read.order,
          // creatableでもupdatableでもないとき、readonlyにする。こうすると、RSheets側の機構で書き込み禁止が実現される
          readonly: field.write === undefined || (column.creatable === false && column.updatable === false),
          deletable: column.deletable,
          columnDeletable: column.columnDeletable,
          // validationをどういう形にするか要検討
          required: column.required === true,
          visible: column.visible,
          // ほぼデバッグ用なので消したい
          // headerPopoverContent: viewState.debugMode ? <JsonSyntaxHighlighter jsonString={JSON.stringify(field)} /> : <></>,
          // eslint-disable-next-line sonarjs/todo-tag
          conditionalEffects: field.meta.conditionalEffects, // TODO
          measures: column.measures,
          dynamicSelectOptionConfig: column.dynamicSelectOptionsConfig,
          // 元々の設計構想だと、config系の値をUIから参照するのは避けていく予定だったが、編集系のUIで結局必要になることが多く、結局いれることにした…
          configField,
        }
      })
      .filter(isNotNullish)
  }, [config.fields, flattenFilterNodeLeafs, nodeNamesWithColors, query, sheetUi])
}

// eslint-disable-next-line sonarjs/function-return-type
function getHighlight(
  rootViewNodeRecord: ViewQueryRecordNode,
  nodePath: NodePath,
  propertyName: string | undefined,
): boolean | boolean[] {
  if (propertyName === undefined) {
    return false
  }

  // eslint-disable-next-line sonarjs/deprecation
  const viewNodeRecord = getRecordNodeFromRootNode(rootViewNodeRecord, nodePath)
  if (viewNodeRecord === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: エラー
    return false
  }

  if (Array.isArray(viewNodeRecord)) {
    return viewNodeRecord.map((x) => (x.meta.highlightAttributeNames ?? []).includes(propertyName))
  }
  return (viewNodeRecord.meta.highlightAttributeNames ?? []).includes(propertyName)
}

export function convertRelationToNodeType(node: ViewQueryNode): RSheetColumn<ViewQueryRecordNode>['nodeType'] {
  const relation = node.read.join?.relation

  switch (relation) {
    case 'one_to_many': {
      return 'children'
    }
    case 'many_to_one': {
      return 'parent'
    }
    case 'one_to_one': {
      return 'child'
    }
    case undefined: {
      return 'root'
    }
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    default: {
      // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
      const x: never = relation
      return 'root'
    }
  }
}
